import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Users, Trash2, Sparkles } from "lucide-react";

export default function AdminTestZone() {
  const { t } = useI18n();
  const [games, setGames] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_game: {} });
  const [form, setForm] = useState({ game_id: "", quantity: 10, nationality: "PT", min_time: 30, max_time: 300, level: 1 });
  const [busy, setBusy] = useState(false);
  const [confirmGlobal, setConfirmGlobal] = useState(false);
  const [lastCreated, setLastCreated] = useState([]);

  const loadStats = async () => {
    try {
      const r = await api.get("/test-zone/stats");
      setStats(r.data || { total: 0, by_game: {} });
    } catch (_) { setStats({ total: 0, by_game: {} }); }
  };

  useEffect(() => {
    api.get("/games").then((r) => {
      const competitionGames = (r.data || []).filter((g) => g.status === "competition");
      setGames(competitionGames);
      if (competitionGames.length) setForm((f) => ({ ...f, game_id: competitionGames[0].id }));
    });
    loadStats();
  }, []);

  const gameCount = form.game_id ? (stats.by_game[form.game_id] || 0) : 0;
  const currentGame = games.find((g) => g.id === form.game_id);
  const useTiers = currentGame?.level_system === "tiers" && Array.isArray(currentGame?.tiers) && currentGame.tiers.length > 0;
  const tierName = (lvl) => {
    if (!useTiers) return `Nível ${lvl}`;
    const t = currentGame.tiers.find((x) => Number(x.level) === Number(lvl));
    return t?.name || `Nível ${lvl}`;
  };

  // When the user switches to a tier-game, snap form.level to the first tier.
  useEffect(() => {
    if (useTiers) {
      const first = currentGame.tiers[0];
      if (first && Number(form.level) !== Number(first.level)) {
        setForm((f) => ({ ...f, level: parseInt(first.level, 10) || 1 }));
      }
    }
    // eslint-disable-next-line
  }, [form.game_id]);

  const generate = async () => {
    if (!form.game_id) { toast.error("Escolhe um jogo"); return; }
    if (!form.quantity || form.quantity < 1) { toast.error("Quantidade tem que ser > 0"); return; }
    if (form.quantity > 1000) { toast.error("Máximo 1000 contas por lote"); return; }
    if (form.min_time >= form.max_time) { toast.error("Tempo mín tem de ser inferior ao máx"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/test-zone/generate", form);
      toast.success(`${data.created} utilizadores de teste criados`);
      setLastCreated((data.credentials || []).slice(0, 10));
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar");
    } finally {
      setBusy(false);
    }
  };

  const removeForGame = async () => {
    if (!form.game_id) return;
    if (!window.confirm(`Eliminar ${gameCount} utilizadores de teste deste jogo?`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/test-zone/${form.game_id}`);
      toast.success(`${data.deleted} eliminados`);
      setLastCreated([]);
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  const removeGlobal = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/test-zone");
      toast.success(`${data.deleted} eliminados globalmente`);
      setConfirmGlobal(false);
      setLastCreated([]);
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <h1 className="text-3xl font-[Outfit] font-semibold text-slate-900 mb-2">{t("test_zone")}</h1>
      <p className="text-sm text-slate-600 mb-6">Gera contas TESTE# em lote para simular rankings e popular o sistema. As contas são marcadas como <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">is_test=true</code> e podem ser eliminadas a qualquer momento.</p>

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-sky-700 font-bold">Total global</div>
          <div className="text-3xl font-[Fredoka] font-bold text-sky-900 mt-1" data-testid="tz-stat-total">{stats.total}</div>
          <div className="text-xs text-sky-700 mt-1">contas TESTE# em todo o sistema</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-emerald-700 font-bold">Neste jogo</div>
          <div className="text-3xl font-[Fredoka] font-bold text-emerald-900 mt-1" data-testid="tz-stat-game">{gameCount}</div>
          <div className="text-xs text-emerald-700 mt-1">para o jogo selecionado</div>
        </div>
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 col-span-2 md:col-span-1">
          <div className="text-xs uppercase tracking-wider text-rose-700 font-bold">Limpeza global</div>
          <Button
            data-testid="tz-delete-global"
            disabled={busy || stats.total === 0}
            onClick={() => setConfirmGlobal(true)}
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-100 mt-2 w-full disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Eliminar TODAS ({stats.total})
          </Button>
        </div>
      </div>

      {/* Per-level breakdown (current game) */}
      {form.game_id && stats.by_game_level && stats.by_game_level[form.game_id] && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6" data-testid="tz-levels-breakdown">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">
            {useTiers ? "Distribuição por escalão (jogo selecionado)" : "Distribuição por nível (jogo selecionado)"}
          </div>
          <div className={`grid gap-2 ${useTiers ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-5" : "grid-cols-5"}`}>
            {(useTiers
              ? currentGame.tiers.map((tr) => ({ key: tr.id, lvl: tr.level, label: tr.name }))
              : [1, 2, 3, 4, 5].map((lv) => ({ key: lv, lvl: lv, label: `Nível ${lv}` }))
            ).map(({ key, lvl, label }) => {
              const cnt = stats.by_game_level[form.game_id][String(lvl)] || 0;
              return (
                <div key={key} className={`rounded-lg p-3 border ${cnt > 0 ? "bg-sky-50 border-sky-200" : "bg-slate-50 border-slate-200 opacity-60"}`} data-testid={`tz-level-stat-${lvl}`}>
                  <div className="text-[10px] font-bold uppercase text-slate-500">{label}</div>
                  <div className="text-2xl font-[Fredoka] font-bold text-slate-900">{cnt}</div>
                  <div className="text-[10px] text-slate-500">{cnt === 1 ? "conta" : "contas"}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {confirmGlobal && (
        <div className="bg-rose-50 border border-rose-300 rounded-xl p-4 mb-6 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-rose-800">Vai eliminar <b>{stats.total}</b> contas TESTE# de TODOS os jogos. Esta ação não pode ser desfeita.</div>
          <div className="flex gap-2">
            <Button data-testid="tz-confirm-global" onClick={removeGlobal} className="bg-rose-600 text-white">Sim, eliminar tudo</Button>
            <Button onClick={() => setConfirmGlobal(false)} variant="outline">Cancelar</Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-xl space-y-3">
        <div>
          <Label>Jogo (competição)</Label>
          <Select value={form.game_id} onValueChange={(v) => setForm({ ...form, game_id: v })}>
            <SelectTrigger data-testid="tz-game"><SelectValue /></SelectTrigger>
            <SelectContent>{games.map((g) => <SelectItem key={g.id} value={g.id} data-testid={`tz-game-${g.id}`}>{g.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>{useTiers ? "Nível/Escalão" : "Nível (1-5)"}</Label>
          {useTiers ? (
            <Select
              value={(() => {
                // Pick the FIRST tier matching the current level number — avoids
                // both items showing as selected when several tiers share a level.
                const first = currentGame.tiers.find((t) => Number(t.level) === Number(form.level));
                return first?.id || currentGame.tiers[0]?.id || "";
              })()}
              onValueChange={(id) => {
                const tr = currentGame.tiers.find((x) => x.id === id);
                if (tr) setForm({ ...form, level: parseInt(tr.level, 10) || 1 });
              }}
            >
              <SelectTrigger data-testid="tz-level"><SelectValue /></SelectTrigger>
              <SelectContent>
                {currentGame.tiers.map((tr) => (
                  <SelectItem key={tr.id} value={tr.id} data-testid={`tz-tier-${tr.id}`}>
                    {tr.name} <span className="text-slate-400 text-xs">(Nv {tr.level})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Select value={String(form.level)} onValueChange={(v) => setForm({ ...form, level: parseInt(v, 10) })}>
              <SelectTrigger data-testid="tz-level"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((n) => (
                  <SelectItem key={n} value={String(n)} data-testid={`tz-level-${n}`}>Nível {n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <p className="text-[11px] text-slate-500 mt-1">
            Os utilizadores criados serão associados ao {useTiers ? "escalão" : "nível"} escolhido e contam para o ranking desse {useTiers ? "escalão" : "nível"}.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Quantidade (1-1000)</Label><Input data-testid="tz-quantity" type="number" min="1" max="1000" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: +e.target.value })} /></div>
          <div><Label>Nacionalidade</Label><Input data-testid="tz-nationality" value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value.toUpperCase() })} /></div>
          <div><Label>Tempo mín (s)</Label><Input data-testid="tz-min-time" type="number" value={form.min_time} onChange={(e) => setForm({ ...form, min_time: +e.target.value })} /></div>
          <div><Label>Tempo máx (s)</Label><Input data-testid="tz-max-time" type="number" value={form.max_time} onChange={(e) => setForm({ ...form, max_time: +e.target.value })} /></div>
        </div>
        <div className="flex gap-3 pt-2">
          <Button data-testid="btn-gen-test" disabled={busy} onClick={generate} className="bg-emerald-600 hover:bg-emerald-700">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Gerar {form.quantity} contas
          </Button>
          <Button data-testid="btn-del-test" disabled={busy || gameCount === 0} onClick={removeForGame} variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50">
            <Trash2 className="w-4 h-4 mr-2" /> Eliminar deste jogo ({gameCount})
          </Button>
        </div>
      </div>

      {lastCreated.length > 0 && (
        <div className="mt-6 bg-white border border-emerald-200 rounded-2xl p-4 max-w-xl">
          <div className="text-sm font-bold text-emerald-700 mb-2 inline-flex items-center gap-2">
            <Users className="w-4 h-4" /> Últimas {lastCreated.length} contas geradas (amostra)
          </div>
          <table className="w-full text-xs">
            <thead className="text-slate-600 border-b border-slate-200">
              <tr><th className="text-left py-1">Nome</th><th className="text-left py-1">Username</th><th className="text-left py-1">Password</th><th className="text-left py-1">Tempo</th></tr>
            </thead>
            <tbody data-testid="tz-created-list">
              {lastCreated.map((c) => (
                <tr key={c.id} className="border-b border-slate-100">
                  <td className="py-1.5 font-medium text-slate-800">{c.name}</td>
                  <td className="py-1.5 font-mono text-slate-600">{c.username}</td>
                  <td className="py-1.5 font-mono text-slate-600">{c.password}</td>
                  <td className="py-1.5 font-mono text-slate-600">{c.best_time?.toFixed(1)}s</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
